import randomSample from "lodash/sample";

import { definedMap } from "@glideapps/ts-necessities";

import { SerializedComponent } from "../../components/SerializedApp";
import {
    ArrayScreenFormat,
    ScreenDescriptionKind,
    PropertyDescription,
    InputOutputTables,
    TableGlideType,
    MutatingScreenKind,
} from "../description";
import { PropertyDescriptor, PropertyTableGetter, ComponentBuilder, ComponentConfiguratorContext } from "../components";
import {
    ImageAspectRatio,
    TilesPadding,
    TextPosition,
    OverlayTextAlignment,
    ImageGravity,
} from "../components/image-types";
import {
    SummaryArrayScreenDescription,
    FallbackInitialsSummaryArrayContentDescription,
    FallbackInitialsSummaryArrayScreenHandler,
} from "./summary-array-screen";
import {
    makeImageHeightPropertyHandler,
    EnumPropertyHandler,
    SwitchPropertyHandler,
} from "../components/property-handlers";
import { PropertySection } from "../components/property-sections";
import { TextStyle } from "../components/text-types";
import { getFeatureSetting } from "../../feature-settings";
import { handleTruncationAndGrouping } from "./truncation-lib";
import { makeEmptyMessage, InlineListBuild } from "./array-content";
import {
    getOverlayPropertyHandlers,
    hasFavoriteButton,
    OverlayContentDescription,
    getOverlayComponents,
    getOverlaySearchProperties,
} from "../overlay-utils";
import {
    isHorizontalPropertyHandler,
    cornersPropertyHandler,
    textSizePropertyHandler,
    allCapsPropertyHandler,
    pushHorizontalAndTruncatePropertyHandlers,
    truncateListPropertyHandler,
} from "./properties";
import { defined } from "../../Support";

export interface TilesArrayContentDescription
    extends FallbackInitialsSummaryArrayContentDescription,
        OverlayContentDescription {
    readonly size: PropertyDescription;
    readonly numColumns: PropertyDescription;
    readonly padding: PropertyDescription;
    readonly isHorizontal: PropertyDescription;
    readonly allowWrapping: PropertyDescription;
    readonly corners: PropertyDescription;
    readonly textPosition: PropertyDescription;
    readonly overlayTextAlignment: PropertyDescription;
    readonly textSize: PropertyDescription;
    readonly allCaps: PropertyDescription;
    readonly gravity: PropertyDescription | undefined;
}

interface TilesArrayScreenDescription extends SummaryArrayScreenDescription, TilesArrayContentDescription {
    readonly kind: ScreenDescriptionKind.Array;
    readonly format: ArrayScreenFormat.Tiles;
}

const defaultSize = ImageAspectRatio.ThreeByOne;
const sizePropertyHandler = makeImageHeightPropertyHandler(defaultSize, false, true, true, "Tile shape");

const gravityPropertyHandler = new EnumPropertyHandler(
    { gravity: ImageGravity.Center },
    "Crop Behavior",
    "Crop Behavior",
    [
        {
            value: ImageGravity.Faces,
            label: "Faces",
            icon: "faceGravity",
        },
        {
            value: ImageGravity.Center,
            label: "Center",
            icon: "centerGravity",
        },
    ],
    PropertySection.Design,
    "small-images"
);

const numberOfColumnsPropertyHandler = new EnumPropertyHandler(
    { numColumns: 1 },
    "Tiles per row",
    "Tiles per row",
    [
        {
            value: 1,
            label: "1",
        },
        {
            value: 2,
            label: "2",
        },
        {
            value: 3,
            label: "3",
        },
        { value: 4, label: "4" },
    ],
    PropertySection.Design,
    "slider"
);

const paddingPropertyHandler = new EnumPropertyHandler(
    { padding: TilesPadding.Loose },
    "Padding",
    "Padding",
    [
        {
            value: TilesPadding.Loose,
            label: "Loose",
            icon: "paddingLoose",
        },
        {
            value: TilesPadding.Tight,
            label: "Tight",
            icon: "paddingTight",
        },
    ],
    PropertySection.Design,
    "small-images"
);

const textPositionPropertyHandler = new EnumPropertyHandler(
    { textPosition: TextPosition.Below },
    "Position",
    "Position",
    [
        {
            value: TextPosition.Below,
            label: "Below",
            icon: "textPositionBelow",
        },
        {
            value: TextPosition.Overlay,
            label: "Overlay",
            icon: "textPositionOverlay",
        },
    ],
    PropertySection.TextStyle,
    "small-images"
);

const overlayTextAlignmentPropertyHandler = new EnumPropertyHandler(
    { overlayTextAlignment: OverlayTextAlignment.Center },
    "Alignment",
    "Alignment",
    [
        {
            value: OverlayTextAlignment.TopLeft,
            label: "Top Left",
            icon: "alignmentTopLeft",
        },
        {
            value: OverlayTextAlignment.BottomLeft,
            label: "Lower Left",
            icon: "alignmentBottomLeft",
        },
        {
            value: OverlayTextAlignment.BottomRight,
            label: "Lower Right",
            icon: "alignmentBottomRight",
        },
        {
            value: OverlayTextAlignment.TopRight,
            label: "Top Right",
            icon: "alignmentTopRight",
        },
        {
            value: OverlayTextAlignment.Center,
            label: "Center",
            icon: "alignmentCenter",
        },
    ],
    PropertySection.TextStyle,
    "small-images"
);

const allowWrappingPropertyHandler = new SwitchPropertyHandler(
    { allowWrapping: false },
    "Allow text wrapping",
    PropertySection.TextStyle
);

function isHorizontal(desc: TilesArrayContentDescription): boolean {
    return isHorizontalPropertyHandler.getEnum(desc);
}

function isCircleShape(desc: TilesArrayContentDescription): boolean {
    return sizePropertyHandler.getEnum(desc) === ImageAspectRatio.Circle;
}

function isOverlay(desc: TilesArrayContentDescription): boolean {
    return textPositionPropertyHandler.getEnum(desc) === TextPosition.Overlay;
}

function overlaysAllowed(desc: TilesArrayContentDescription): boolean {
    return !isCircleShape(desc) && numberOfColumnsPropertyHandler.getEnum(desc) < 3;
}

export class TilesArrayScreenHandler extends FallbackInitialsSummaryArrayScreenHandler<
    TilesArrayContentDescription,
    TilesArrayScreenDescription
> {
    protected readonly supportsNonURLImages = true;
    protected readonly supportsEmojiImages = false;
    protected readonly fullTitleFallback = true;
    // We only support optional truncation when vertical
    protected readonly supportsTruncateList = false;

    constructor() {
        super(ArrayScreenFormat.Tiles, "Tiles", "columns", true);
    }

    protected titleRequired() {
        return false;
    }

    protected get supportsGrouping(): boolean {
        return true;
    }

    // FIXME: Unify with Cards
    public getFavoritePivots(
        desc: TilesArrayContentDescription,
        table: TableGlideType
    ): ReadonlyMap<TableGlideType, string | true> | undefined {
        if (!hasFavoriteButton(desc, overlaysAllowed)) return undefined;

        return new Map([[table, true]]);
    }

    protected contentWithFallbackInitials(
        builder: ComponentBuilder,
        desc: TilesArrayContentDescription,
        table: TableGlideType,
        arrayData: SerializedComponent,
        title: SerializedComponent,
        subtitle: SerializedComponent | undefined,
        image: SerializedComponent | undefined,
        initials: SerializedComponent | undefined,
        onTap: SerializedComponent | undefined,
        onDropImage: SerializedComponent | undefined,
        searchActiveFlag: SerializedComponent | undefined,
        inlineListBuild: InlineListBuild | undefined,
        caption: SerializedComponent | undefined,
        itemID: SerializedComponent | undefined
    ): SerializedComponent {
        const { b } = builder;
        const size = b.constant(sizePropertyHandler.getEnum(desc));
        const numColumnsRaw = numberOfColumnsPropertyHandler.getEnum(desc);
        const numColumns = b.constant(numColumnsRaw);
        const gravity = b.constant(gravityPropertyHandler.getEnum(desc));
        const allowWrapping = b.constant(allowWrappingPropertyHandler.getSwitch(desc));
        const cornersRaw = cornersPropertyHandler.getEnum(desc);
        const corners = b.constant(cornersRaw);
        const textPosition = b.constant(textPositionPropertyHandler.getEnum(desc));
        const overlayTextAlignmentEnum = overlayTextAlignmentPropertyHandler.getEnum(desc);
        const overlayTextAlignment = b.constant(overlayTextAlignmentEnum);
        const textSizeRaw = textSizePropertyHandler.getEnum(desc);
        const textSize = b.constant(textSizeRaw);
        const allCaps = allCapsPropertyHandler.getSwitch(desc);
        const emptyMessage = makeEmptyMessage(b, searchActiveFlag, inlineListBuild);

        const textStyle = b.constant(allCaps ? TextStyle.AllCaps : TextStyle.Normal);

        const [topLeftOverlay, topRightOverlay, bottomLeftOverlay, bottomRightOverlay] = getOverlayComponents(
            desc,
            builder,
            table,
            undefined,
            isOverlay(desc) && overlayTextAlignmentEnum === OverlayTextAlignment.BottomRight,
            cornersRaw,
            textSizeRaw
        );

        const overlays = overlaysAllowed(desc);

        if (isHorizontal(desc)) {
            return handleTruncationAndGrouping(
                builder,
                desc,
                table,
                arrayData,
                inlineListBuild,
                caption,
                numColumnsRaw,
                true,
                (listData, isGrouped, args) =>
                    b.ui("HorizontalTiles", {
                        list: listData,
                        numColumns,
                        caption,
                        isGrouped,
                        emptyMessage,
                        item: b.ui("HorizontalTilesItem", {
                            title,
                            subtitle,
                            image,
                            size,
                            fallbackInitials: initials,
                            onTap,
                            onDropImage,
                            corners,
                            textPosition,
                            overlayTextAlignment,
                            textSize,
                            textStyle,
                            gravity,
                            itemID,
                            topLeftOverlay: overlays ? topLeftOverlay : undefined,
                            topRightOverlay: overlays ? topRightOverlay : undefined,
                            bottomLeftOverlay: overlays ? bottomLeftOverlay : undefined,
                            bottomRightOverlay: overlays ? bottomRightOverlay : undefined,
                        }),
                        ...args,
                    })
            );
        } else {
            const padding = b.constant(paddingPropertyHandler.getEnum(desc));
            return handleTruncationAndGrouping(
                builder,
                desc,
                table,
                arrayData,
                inlineListBuild,
                caption,
                numColumnsRaw,
                true,
                (listData, isGrouped, args) =>
                    b.ui("VerticalTiles", {
                        list: listData,
                        numColumns,
                        padding,
                        emptyMessage,
                        isGrouped,
                        item: b.ui("VerticalTilesItem", {
                            title,
                            subtitle,
                            image,
                            size,
                            padding,
                            fallbackInitials: initials,
                            onTap,
                            onDropImage,
                            allowWrapping,
                            corners,
                            textPosition,
                            overlayTextAlignment,
                            textSize,
                            textStyle,
                            gravity,
                            itemID,
                            topLeftOverlay: overlays ? topLeftOverlay : undefined,
                            topRightOverlay: overlays ? topRightOverlay : undefined,
                            bottomLeftOverlay: overlays ? bottomLeftOverlay : undefined,
                            bottomRightOverlay: overlays ? bottomRightOverlay : undefined,
                        }),
                        allowWrapping,
                        textPosition,
                        caption,
                        ...args,
                    })
            );
        }
    }

    public randomContentDescription(
        tables: InputOutputTables,
        ccc: ComponentConfiguratorContext,
        insideInlineList: boolean,
        mutatingScreenKind: MutatingScreenKind | undefined,
        isDefaultArrayScreen: boolean
    ): TilesArrayContentDescription | undefined {
        const desc = this.defaultContentDescription(
            tables,
            ccc,
            insideInlineList,
            mutatingScreenKind,
            isDefaultArrayScreen
        );
        if (desc === undefined) return undefined;

        const size = sizePropertyHandler.getRandom();
        const numberOfColumns = defined(randomSample([1, 2] as const));
        const padding = paddingPropertyHandler.getRandom();
        const allowWrapping = allowWrappingPropertyHandler.getRandom();
        const corners = cornersPropertyHandler.getRandom();
        const textSize = textSizePropertyHandler.getRandom();
        const textPosition = textPositionPropertyHandler.getRandom();
        let textAlignment: OverlayTextAlignment | undefined;
        if (textPosition === TextPosition.Overlay) {
            textAlignment = overlayTextAlignmentPropertyHandler.getRandom();
        }
        const allCaps = allCapsPropertyHandler.getRandom();

        return {
            ...desc,
            ...sizePropertyHandler.getProperty(size),
            ...numberOfColumnsPropertyHandler.getProperty(numberOfColumns),
            ...paddingPropertyHandler.getProperty(padding),
            ...allowWrappingPropertyHandler.getProperty(allowWrapping),
            ...cornersPropertyHandler.getProperty(corners),
            ...textSizePropertyHandler.getProperty(textSize),
            ...textPositionPropertyHandler.getProperty(textPosition),
            ...definedMap(textAlignment, a => overlayTextAlignmentPropertyHandler.getProperty(a)),
            ...allCapsPropertyHandler.getProperty(allCaps),
        };
    }

    protected getEnableFallbackInitials(desc: TilesArrayScreenDescription): boolean {
        return desc.titleProperty !== undefined;
    }

    public getAdditionalPropertyDescriptors<T extends TilesArrayContentDescription>(
        getPropertyTable: PropertyTableGetter<T> | undefined,
        _insideInlineList: boolean,
        desc: T,
        isDefaultArrayScreen: boolean
    ): ReadonlyArray<PropertyDescriptor> {
        const descrs: PropertyDescriptor[] = [];
        descrs.push(sizePropertyHandler, numberOfColumnsPropertyHandler);
        pushHorizontalAndTruncatePropertyHandlers(descrs, desc, isDefaultArrayScreen, true);
        if (!isCircleShape(desc)) {
            descrs.push(textPositionPropertyHandler);
            descrs.push(cornersPropertyHandler);
        }

        if (isOverlay(desc) && !isCircleShape(desc)) {
            descrs.push(overlayTextAlignmentPropertyHandler);
        }
        descrs.push(textSizePropertyHandler);
        if (!isHorizontal(desc) && !isCircleShape(desc)) {
            descrs.push(paddingPropertyHandler);
        }
        descrs.push();
        if (getFeatureSetting("imageGravity")) {
            descrs.push(gravityPropertyHandler);
        }
        if (!isOverlay(desc) && !isHorizontal(desc)) {
            descrs.push(allowWrappingPropertyHandler);
        }
        descrs.push(allCapsPropertyHandler);

        if (!overlaysAllowed(desc)) {
            return descrs;
        }

        descrs.push(...getOverlayPropertyHandlers(getPropertyTable, undefined));

        return descrs;
    }

    public getBasicSearchProperties(desc: TilesArrayContentDescription): readonly string[] {
        return [...super.getBasicSearchProperties(desc), ...getOverlaySearchProperties(desc)];
    }

    public adjustContentDescriptionAfterUpdate(
        desc: TilesArrayContentDescription,
        updates: Partial<TilesArrayContentDescription> | undefined
    ): TilesArrayContentDescription {
        if (updates?.isHorizontal !== undefined && isHorizontalPropertyHandler.getEnum(desc)) {
            desc = truncateListPropertyHandler.setInDescription(desc, truncateListPropertyHandler.defaultValue);
        }
        return desc;
    }
}
